import { clients } from '@/lib/https';
import { ModuleName } from '@/constant/modules.constants';
import {
  GetFilesPayload,
  GetFilesResponse,
  GetObjectsPayload,
  GetObjectsResponse,
  GetPreSignedUrlForUploadPayload,
  GetPreSignedUrlForUploadResponse,
  StorageFileItem,
} from '../types/storage.types';

/**
 * Every storage call goes through the same gateway as IAM: the session is an httpOnly cookie
 * scoped to that host, so a different host (e.g. data.seliseblocks.com) never receives it.
 */
const DATA_API_BASE_URL = (
  import.meta.env.VITE_API_BASE_URL || 'https://blocksapi.slsblx.com'
).replace(/\/$/, '');

const STORAGE_CONFIGURATION_NAME = 'Default';

/** Campaign / template media library folder in Blocks object storage. */
export const MEDIA_LIBRARY_PARENT_DIRECTORY_ID =
  import.meta.env.VITE_MEDIA_LIBRARY_PARENT_DIRECTORY_ID ||
  'd9b2d3d4-1331-4e08-9ac5-c346efa6f715';

const DEFAULT_OBJECTS_LIMIT = 50;

/** Session cache of file URLs so list/search does not re-hit get-files. */
const fileUrlCache = new Map<string, string>();

export const getPreSignedUrlForUpload = async (
  payload: GetPreSignedUrlForUploadPayload
): Promise<GetPreSignedUrlForUploadResponse> => {
  const response = await clients.post<GetPreSignedUrlForUploadResponse>(
    `${DATA_API_BASE_URL}/data/v4/Files/GetPreSignedUrlForUpload`,
    JSON.stringify(payload)
  );
  return response;
};

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const readString = (...values: unknown[]): string => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
};

/** Normalize Blocks camelCase / PascalCase file/object rows into one shape. */
export const normalizeStorageFileItem = (raw: unknown): StorageFileItem => {
  const row = asRecord(raw) || {};
  const nested = asRecord(row.properties) || asRecord(row.Properties) || {};
  const sizeRaw =
    row.sizeInBytes ??
    row.SizeInBytes ??
    row.size ??
    row.Size ??
    nested.sizeInBytes ??
    nested.size;

  return {
    itemId:
      readString(
        row.itemId,
        row.ItemId,
        row.fileId,
        row.FileId,
        row.id,
        row.Id,
        row.objectId,
        row.ObjectId,
        nested.itemId,
        nested.id
      ) || null,
    url:
      readString(
        row.url,
        row.Url,
        row.URL,
        row.fileUrl,
        row.FileUrl,
        row.downloadUrl,
        row.DownloadUrl,
        row.contentUrl,
        row.ContentUrl,
        row.publicUrl,
        row.PublicUrl,
        nested.url,
        nested.Url,
        nested.fileUrl
      ) || null,
    name:
      readString(
        row.name,
        row.Name,
        row.fileName,
        row.FileName,
        row.artifactName,
        row.ArtifactName,
        row.systemName,
        row.SystemName,
        nested.name,
        nested.fileName
      ) || null,
    parentDirectoryID:
      readString(
        row.parentDirectoryID,
        row.ParentDirectoryID,
        row.parentDirectoryId,
        row.ParentDirectoryId,
        nested.parentDirectoryId
      ) || null,
    systemName: readString(row.systemName, row.SystemName) || null,
    type: typeof row.type === 'number' ? row.type : undefined,
    typeString:
      readString(
        row.typeString,
        row.TypeString,
        row.type,
        row.Type,
        row.contentType,
        row.ContentType,
        row.mimeType,
        row.MimeType,
        row.extension,
        row.Extension,
        nested.mimeType
      ) || null,
    sizeInBytes: typeof sizeRaw === 'number' ? sizeRaw : undefined,
    accessModifier: readString(row.accessModifier, row.AccessModifier) || undefined,
    metaData: (row.metaData ?? row.MetaData) as Record<string, unknown> | null | undefined,
    createdAt:
      readString(
        row.createdAt,
        row.CreatedAt,
        row.createdDate,
        row.CreatedDate,
        nested.createdAt,
        nested.createdDate
      ) || null,
    createdDate:
      readString(
        row.createdDate,
        row.CreatedDate,
        row.createdAt,
        row.CreatedAt,
        nested.createdDate,
        nested.createdAt
      ) || null,
    updatedAt:
      readString(
        row.updatedAt,
        row.UpdatedAt,
        row.modifiedDate,
        row.ModifiedDate,
        nested.updatedAt
      ) || null,
    width:
      typeof row.width === 'number'
        ? row.width
        : typeof nested.width === 'number'
          ? nested.width
          : undefined,
    height:
      typeof row.height === 'number'
        ? row.height
        : typeof nested.height === 'number'
          ? nested.height
          : undefined,
  };
};

const extractObjectRows = (payload: unknown): unknown[] => {
  if (Array.isArray(payload)) return payload;
  const row = asRecord(payload);
  if (!row) return [];
  const data =
    row.objects ??
    row.Objects ??
    row.data ??
    row.Data ??
    row.items ??
    row.Items ??
    row.files ??
    row.Files ??
    row.result ??
    row.Result ??
    row.value ??
    row.Value;
  return Array.isArray(data) ? data : [];
};

/**
 * Bulk file details (including public URLs) for the given ids.
 * POST `…/data/v4/files/get-files` with `{ fileIds, projectKey, configurationName }`.
 */
export const getFiles = async (
  payload: GetFilesPayload | string[]
): Promise<GetFilesResponse> => {
  const fileIds = (Array.isArray(payload) ? payload : payload.fileIds || [])
    .map((id) => id.trim())
    .filter(Boolean);
  const uniqueIds = [...new Set(fileIds)];

  if (uniqueIds.length === 0) {
    return { data: [], isSuccess: true };
  }

  const projectKey = import.meta.env.VITE_X_BLOCKS_KEY || '';
  const response = await clients.post<unknown>(
    `${DATA_API_BASE_URL}/data/v4/files/get-files`,
    JSON.stringify({
      fileIds: uniqueIds,
      projectKey,
      configurationName: STORAGE_CONFIGURATION_NAME,
    })
  );

  const row = asRecord(response) || {};
  if (row.errors && typeof row.errors === 'object') {
    const errors = row.errors as Record<string, string>;
    if (Object.keys(errors).length > 0) {
      throw new Error(Object.values(errors).join(', ') || 'Failed to get files.');
    }
  }

  if (row.isSuccess === false) {
    throw new Error('Failed to get files.');
  }

  const data = extractObjectRows(response).map(normalizeStorageFileItem);
  for (const item of data) {
    const id = (item.itemId || '').trim();
    const url = (item.url || '').trim();
    if (id && url) {
      fileUrlCache.set(id, url);
    }
  }

  return { data, isSuccess: true };
};

/**
 * Lists media-library objects, then resolves thumbnail URLs in one get-files call
 * (cached per fileId — search / re-open reuse the cache).
 * GET `…/data/v4/objects/get-objects?parentDirectoryId=…&limit=…&search=…`
 */
export const getObjects = async (
  payload: GetObjectsPayload = {}
): Promise<GetObjectsResponse> => {
  const parentDirectoryId =
    payload.parentDirectoryId?.trim() || MEDIA_LIBRARY_PARENT_DIRECTORY_ID;
  const limit =
    payload.limit && payload.limit > 0 ? payload.limit : DEFAULT_OBJECTS_LIMIT;
  const search = payload.search?.trim() || '';

  const params = new URLSearchParams({
    parentDirectoryId,
    limit: String(limit),
  });
  if (search) {
    params.set('search', search);
  }

  const response = await clients.get<unknown>(
    `${DATA_API_BASE_URL}/data/v4/objects/get-objects?${params.toString()}`
  );

  const row = asRecord(response) || {};
  if (row.errors && typeof row.errors === 'object') {
    const errors = row.errors as Record<string, string>;
    if (Object.keys(errors).length > 0) {
      throw new Error(Object.values(errors).join(', ') || 'Failed to list objects.');
    }
  }

  if (row.isSuccess === false) {
    throw new Error('Failed to list objects.');
  }

  const catalog = extractObjectRows(response).map(normalizeStorageFileItem);
  const totalCount =
    typeof row.totalCount === 'number'
      ? row.totalCount
      : typeof row.TotalCount === 'number'
        ? row.TotalCount
        : typeof row.count === 'number'
          ? row.count
          : catalog.length;

  const missingIds = catalog
    .map((entry) => (entry.itemId || '').trim())
    .filter((id) => {
      if (!id) return false;
      const catalogUrl = catalog.find((entry) => (entry.itemId || '').trim() === id)?.url;
      if ((catalogUrl || '').trim()) return false;
      return !fileUrlCache.has(id);
    });

  if (missingIds.length > 0) {
    try {
      await getFiles({ fileIds: missingIds });
    } catch (error) {
      console.warn('[storage] get-files failed', error);
    }
  }

  const data = catalog.map((entry) => {
    const fileId = (entry.itemId || '').trim();
    const url = (entry.url || (fileId ? fileUrlCache.get(fileId) : '') || '').trim();
    return url ? { ...entry, url } : entry;
  });

  return { data, totalCount, isSuccess: true };
};

const putFileToUploadUrl = async (uploadUrl: string, file: File) => {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
      'x-ms-blob-type': 'BlockBlob',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to upload file (${response.status})`);
  }

  return uploadUrl.split('?')[0];
};

export const uploadPublicFile = async (
  file: File,
  moduleName: ModuleName = ModuleName.DefaultCloud
): Promise<{ fileId: string; fileUrl: string }> => {
  const projectKey = import.meta.env.VITE_X_BLOCKS_KEY || '';
  const parentDirectoryId = MEDIA_LIBRARY_PARENT_DIRECTORY_ID;

  const uploadSource = await getPreSignedUrlForUpload({
    name: file.name,
    projectKey,
    metaData: '',
    accessModifier: 'Public',
    configurationName: STORAGE_CONFIGURATION_NAME,
    parentDirectoryId,
    tags: '',
    moduleName,
  });

  if (!uploadSource.isSuccess || !uploadSource.uploadUrl) {
    const errorMessage = Object.values(uploadSource.errors ?? {}).join(', ');
    throw new Error(errorMessage || 'Failed to get upload URL');
  }

  const fileUrl = await putFileToUploadUrl(uploadSource.uploadUrl, file);
  const fileId = uploadSource.fileId ?? '';
  if (fileId && fileUrl) {
    fileUrlCache.set(fileId, fileUrl);
  }

  return { fileId, fileUrl };
};

const IMAGE_FILE_PATTERN = /\.(png|jpe?g|gif|webp|svg)$/i;

/** Prefer image files for the MailCraft Media Library grid. */
export const isStorageImageFile = (file: StorageFileItem): boolean => {
  const typeString = (file.typeString || '').toLowerCase();
  if (typeString.includes('directory') || typeString.includes('folder')) {
    return false;
  }
  if (typeString.includes('image')) {
    return true;
  }

  const name = file.name || file.systemName || '';
  if (IMAGE_FILE_PATTERN.test(name)) {
    return true;
  }

  const urlPath = (file.url || '').split('?')[0];
  if (IMAGE_FILE_PATTERN.test(urlPath)) {
    return true;
  }

  // Keep any non-folder row that has a resolvable public URL.
  return Boolean((file.url || '').trim());
};

export const deleteFile = async (
  fileId: string,
  permanent = false
): Promise<{ isSuccess: boolean }> => {
  const projectKey = import.meta.env.VITE_X_BLOCKS_KEY || '';
  try {
    const response = await clients.post<unknown>(
      `${DATA_API_BASE_URL}/data/v4/files/delete-file`,
      JSON.stringify({
        fileId,
        permanent,
        configurationName: STORAGE_CONFIGURATION_NAME,
        projectKey,
      })
    );
    const row = asRecord(response) || {};
    if (row.isSuccess === false) {
      const errors = (row.errors as Record<string, string>) || {};
      const msg = Object.values(errors).join(', ');
      throw new Error(msg || 'Failed to delete file');
    }
  } catch (error) {
    try {
      await clients.delete<unknown>(
        `${DATA_API_BASE_URL}/data/v4/Files/Delete?fileId=${encodeURIComponent(fileId)}`
      );
    } catch {
      console.warn('[storage] file delete fallback also failed', error);
      throw error;
    }
  }

  fileUrlCache.delete(fileId);
  return { isSuccess: true };
};
